import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
} from 'firebase/app-check';
import {
  getAuth,
  GoogleAuthProvider,
  connectAuthEmulator,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import { getMessaging, isSupported as isMessagingSupported } from 'firebase/messaging';
// Firebase Storage NO se usa: tras la migración 2026-05-19 las fotos viven
// en Supabase Storage (bucket privado pet-photos) accedidas via signed URLs
// firmadas por el worker externo. Ver src/services/storageService.js.

/**
 * Validación temprana de variables de entorno.
 * Falla rápido en build/runtime si falta config crítica.
 */
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_APP_ID',
];

requiredEnvVars.forEach((key) => {
  if (!import.meta.env[key]) {
    throw new Error(`[firebase.js] Missing required env var: ${key}`);
  }
});

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Singleton — evita re-inicialización en HMR
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// =============================================================================
// App Check — debe inicializarse ANTES que el resto de los servicios.
// =============================================================================
// App Check ata cada request al Firebase backend a un "attestation token"
// que demuestra que la request viene de una instancia legítima de la app
// (no de un script o cliente fabricado). En web usamos reCAPTCHA v3 como
// proveedor — silencioso para el usuario, devuelve un score.
//
// Solo se llama si VITE_RECAPTCHA_SITE_KEY está seteada. En desarrollo es
// opcional (warning si falta); en producción es requerido (FAIL del build
// vía requiredEnvVars no aplica todavía — es decisión consciente: queremos
// que un olvido en .env.local de un dev no rompa su flujo local, pero el
// deploy a prod sin la key generaría requests sin token que el backend
// rechaza si el enforcement está activo).
//
// En DEV usamos el "debug token" provider de Firebase: setear
// `self.FIREBASE_APPCHECK_DEBUG_TOKEN = true` ANTES de initializeAppCheck
// hace que Firebase genere y loguee un token de debug en la consola del
// browser. Ese token se registra una sola vez en Firebase Console →
// App Check → Debug Tokens y queda válido por la vida configurada.
// =============================================================================

if (typeof window !== 'undefined') {
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  if (import.meta.env.DEV) {
    // Si el dev pegó un debug token persistente en .env.local, usamos ése.
    // Sino, `true` hace que Firebase genere uno automático y lo loguee.
    const explicitDebugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
    // self es global del browser; en SSR / Node-only puede no existir.
    // eslint-disable-next-line no-undef
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = explicitDebugToken || true;
  }

  if (siteKey) {
    try {
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(siteKey),
        // Renovar el token automáticamente antes de que expire (~1h).
        // Sin esto, requests largas pueden quedar con tokens vencidos.
        isTokenAutoRefreshEnabled: true,
      });
    } catch (err) {
      // No tirar el error: si App Check falla al inicializar (ej. dominio
      // no autorizado en el panel de reCAPTCHA), la app sigue funcionando
      // siempre que el enforcement en Firebase Console NO esté activado.
      // Mostramos un warning explícito para que se note en dev.
      console.warn(
        '[firebase.js] App Check no pudo inicializarse. Las requests al backend funcionan mientras enforcement esté inactivo. Detalle:',
        err
      );
    }
  } else if (!import.meta.env.DEV) {
    // En prod, sin site key, el deploy queda vulnerable. Lo flag como error
    // pero no rompemos (algunas builds de prod pueden necesitar este escape).
    console.error(
      '[firebase.js] VITE_RECAPTCHA_SITE_KEY no está configurada en producción. App Check NO está activo.'
    );
  }
}

// --- Auth ---
export const auth = getAuth(app);
setPersistence(auth, browserLocalPersistence).catch((err) => {
  console.error('[firebase.js] Failed to set auth persistence:', err);
});

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

// --- Firestore con caché persistente multi-tab ---
// Crítico para PWA: la app sigue funcionando offline y sincroniza al recuperar conexión.
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
});

// --- Cloud Messaging (lazy, solo si el navegador lo soporta) ---
export const getMessagingInstance = async () => {
  try {
    const supported = await isMessagingSupported();
    return supported ? getMessaging(app) : null;
  } catch (err) {
    console.warn('[firebase.js] FCM not supported:', err);
    return null;
  }
};

// --- Emuladores (solo en dev) ---

const shouldUseEmulators =
  (import.meta.env.DEV && import.meta.env.VITE_USE_FIRESTORE_EMULATOR === 'true') ||
  // Detección automática en entorno de test
  (typeof process !== 'undefined' && process.env.FIRESTORE_EMULATOR_HOST);

if (shouldUseEmulators) {
  const host =
    (typeof process !== 'undefined' && process.env.FIRESTORE_EMULATOR_HOST?.split(':')[0]) ||
    import.meta.env.VITE_EMULATOR_HOST ||
    'localhost';
  try {
    connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
    connectFirestoreEmulator(db, host, 8080);
    if (import.meta.env.DEV) {
      console.info('[firebase.js] Connected to local emulators');
    }
  } catch (err) {
    console.error('[firebase.js] Emulator connection failed:', err);
  }
}

export default app;